import { Loader, Paper, SegmentedControl, Stack, Switch, Text } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { useEffect, useState, type CSSProperties } from "react";

import { useListMyTeams } from "../../api/generated/endpoints/teams/teams";
import { Tier, type AchievementBoard, type AchievementView, type Stage, type UnseenAward } from "../../api/generated/model";
import { Badge } from "../../platform/achievements/Badge";
import { useBoard, useHiddenSwitch, useMarkSeen, useSharingSwitch } from "../../platform/achievements/board";
import { STAGE_NAME, TIER_GLYPH, TIER_NAME, badgeState, earnedOn, tierColor } from "../../platform/achievements/words";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { AccountFrame } from "../../platform/shell/AccountFrame";
import classes from "./achievements.module.css";

/** Which cards the tab lists: the mockup's All, Earned and Locked. */
type Shown = "all" | "earned" | "locked";

const earned = (view: AchievementView) => view.earned_in.length > 0;

/** The board's achievements by stage, in its order: the stage's, then earned first. */
function byStage(views: readonly AchievementView[]): [Stage, AchievementView[]][] {
  const stages = new Map<Stage, AchievementView[]>();
  for (const view of views) stages.set(view.stage, [...(stages.get(view.stage) ?? []), view]);
  return [...stages];
}

/** The person's teams by id, for the words "Earned in eJam" and "Share in eJam". */
type TeamNames = ReadonlyMap<string, string>;

function useTeamNames(): TeamNames {
  const teams = useListMyTeams().data ?? [];
  return new Map(teams.map((team) => [team.id, team.name]));
}

/** "eJam and Demo": the teams it was earned in, by name, earliest first; none for an account award. */
function earnedWhere(view: AchievementView, names: TeamNames): string {
  const found = view.earned_in.flatMap((place) => (place.team_id === null ? [] : (names.get(place.team_id) ?? [])));
  const each = [...new Set(found)];
  return each.length < 2 ? (each[0] ?? "") : `${each.slice(0, -1).join(", ")} and ${each.at(-1) ?? ""}`;
}

/**
 * The unlocks unseen when the tab opened: each develops once on this visit and is marked seen, so
 * the next visit shows it earned. The opening is the first board the tab shows that is visible and
 * `read` on this visit: one kept from an earlier visit waits for the read the tab starts, and a
 * hidden one for Hide to be turned off, which shows what it kept. One that arrives later keeps its
 * own card (spec web/achievements, decision 2). A refused mark is `error`.
 */
function useFresh(board: AchievementBoard, read: boolean) {
  const [opened, setOpened] = useState<readonly UnseenAward[] | null>(null);
  if (opened === null && read && !board.hidden) setOpened(board.unseen);
  const { mark, error } = useMarkSeen();
  useEffect(() => {
    if (opened !== null && opened.length > 0) mark(opened.map((each) => each.award_id));
  }, [opened, mark]);
  return { fresh: new Set((opened ?? []).map((each) => each.achievement_id)), error };
}

function Meta({ view, names }: { view: AchievementView; names: TeamNames }) {
  const first = view.earned_in.map((place) => place.earned_at).sort()[0];
  if (first !== undefined) {
    const where = earnedWhere(view, names);
    return (
      <Text className={classes.meta}>
        Earned {earnedOn(first)}
        {where === "" ? "" : ` in ${where}`}
      </Text>
    );
  }
  if (view.progress === null) return null;
  const { have, need } = view.progress;
  const words = `${have.toLocaleString("en-US")} of ${need.toLocaleString("en-US")}`;
  return (
    <>
      <progress className={classes.bar} value={have} max={need} aria-label={words} />
      <Text className={classes.progress}>{words}</Text>
    </>
  );
}

function Card({ view, fresh, names }: { view: AchievementView; fresh: boolean; names: TeamNames }) {
  const state = badgeState(view, fresh);
  return (
    <article className={classes.card} data-state={state} style={{ "--tier": tierColor(view.tier) } as CSSProperties} aria-label={view.name}>
      <Badge tier={view.tier} glyph={view.glyph} state={state} size={64} />
      <div>
        <div className={classes.top}>
          <h4 className={classes.name}>{view.name}</h4>
          <span className={classes.tier}>{TIER_NAME[view.tier]}</span>
        </div>
        <Text className={classes.roast}>{view.description ?? `Secret. Hint: ${view.hint ?? ""}`}</Text>
        {fresh ? <Text className={classes.fresh}>New</Text> : null}
        <Meta view={view} names={names} />
      </div>
    </article>
  );
}

function Switches({ board, names }: { board: AchievementBoard; names: TeamNames }) {
  const hide = useHiddenSwitch();
  const share = useSharingSwitch();
  return (
    <Stack gap={10} className={classes.switches}>
      {board.hidden ? null : (
        <>
          <Text className={classes.help}>Share with each team what you earned in it. Private until you turn it on.</Text>
          {board.sharing.map((team) => (
            <Switch
              key={team.team_id}
              label={`Share in ${names.get(team.team_id) ?? "this team"}`}
              checked={team.shared}
              onChange={(event) => share.mutate({ teamId: team.team_id, data: { shared: event.currentTarget.checked } })}
            />
          ))}
        </>
      )}
      <Switch
        label="Hide achievements"
        checked={board.hidden}
        onChange={(event) => hide.mutate({ data: { hidden: event.currentTarget.checked } })}
      />
      {share.isError ? <ErrorAlert error={share.error} /> : null}
      {hide.isError ? <ErrorAlert error={hide.error} /> : null}
    </Stack>
  );
}

/** Each tier's chip: its badge, and how many of its achievements are earned, as the mockup counts. */
function TierChips({ board }: { board: AchievementBoard }) {
  return (
    <div className={classes.chips}>
      {Object.values(Tier).map((tier) => {
        const all = board.achievements.filter((view) => view.tier === tier);
        return (
          <span key={tier} className={classes.chip}>
            <Badge tier={tier} glyph={TIER_GLYPH[tier]} state="earned" size={26} />
            {TIER_NAME[tier]}{" "}
            <b>
              {all.filter(earned).length} of {all.length}
            </b>
          </span>
        );
      })}
    </div>
  );
}

function Board({ board, read }: { board: AchievementBoard; read: boolean }) {
  const { fresh, error } = useFresh(board, read);
  const names = useTeamNames();
  const [shown, setShown] = useState<Shown>("all");
  const { quiet } = board;
  const listed = (view: AchievementView) => shown === "all" || (shown === "earned") === earned(view);
  return (
    <Stack gap={20}>
      {error === null ? null : <ErrorAlert error={error} />}
      <Paper className={classes.summary}>
        {board.hidden ? (
          <div>
            <Text className={classes.hiddenTitle}>Achievements are hidden</Text>
            <Text className={classes.help}>
              No count, no cards and no board. You keep earning them, so turning this back on shows everything you
              earned meanwhile.
            </Text>
          </div>
        ) : (
          <div>
            <Text className={classes.big}>
              {board.earned} <span className={classes.of}>of {board.offered} earned</span>
            </Text>
            <Text className={classes.help}>
              {board.secrets_found} of {board.secrets} secrets found
            </Text>
            <Text className={classes.help}>Across your teams</Text>
            <TierChips board={board} />
          </div>
        )}
        <Switches board={board} names={names} />
      </Paper>
      {board.hidden ? null : (
        <>
          {quiet > 0 ? (
            <Text className={classes.quiet}>
              You'd already earned {quiet} {quiet === 1 ? "achievement" : "achievements"} before we started counting.
            </Text>
          ) : null}
          <SegmentedControl
            aria-label="Show"
            className={classes.filter}
            value={shown}
            onChange={(value) => setShown(value as Shown)}
            data={[
              { value: "all", label: "All" },
              { value: "earned", label: "Earned" },
              { value: "locked", label: "Locked" },
            ]}
          />
          {byStage(board.achievements).map(([stage, views]) => (
            <section key={stage} aria-label={STAGE_NAME[stage]} hidden={!views.some(listed)}>
              <h3 className={classes.stage}>
                {STAGE_NAME[stage]}{" "}
                <span>
                  {views.filter(earned).length} of {views.length}
                </span>
              </h3>
              <div className={classes.grid}>
                {views.filter(listed).map((view, at) => (
                  <Card key={view.id ?? `secret-${at}`} view={view} fresh={view.id !== null && fresh.has(view.id)} names={names} />
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </Stack>
  );
}

/** The Account page's Achievements tab (spec web/achievements, 2). */
export function AchievementsScreen() {
  const { teamId } = useParams({ from: "/signed-in/teams/$teamId/account/achievements" });
  return (
    <AccountFrame teamId={teamId}>
      <Achievements />
    </AccountFrame>
  );
}

/**
 * The tab's body, the person's board, whatever frame draws it: the Account page's, or Pro's
 * Settings (spec ui-perf/pro-studio-redesign, decision 35).
 */
export function Achievements() {
  const board = useBoard();
  // A board kept from an earlier visit shows at once. The tab opens on a read from the server: the
  // board it opened with, if fresh, else one that lands after. A write's change to the board keeps
  // its read's time, so it never passes for a read (decision 2).
  const [opening] = useState(() => ({ at: board.dataUpdatedAt, fresh: board.data !== undefined && !board.isStale }));
  const read = opening.fresh || board.dataUpdatedAt > opening.at;
  return (
    <>
      {board.isError ? <ErrorAlert error={board.error} /> : null}
      {board.data === undefined ? (
        board.isError ? null : (
          <Loader size="sm" aria-label="Loading your achievements" />
        )
      ) : (
        <Board board={board.data} read={read} />
      )}
    </>
  );
}
